import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, tap, catchError, throwError, map, BehaviorSubject, of, filter, take, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

export interface IUser {
  email: string;
  token?: string;
}

export interface UserTenantInfo {
  tenantId: number;
  tenantName: string;
}
interface DecodedToken {
  tokenKey: string;
  TenantId: string;
  UserId: string;
  SubscriptionStatus: string;
  AccountType?: string;
  role?: string;  // The role claim from the token
  exp: number;
  iss: string;
  aud: string;
  [key: string]: any;  // For any additional properties
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user: IUser | null = null;
  private _token: string | null = null;
  private subscriptionStatus = new BehaviorSubject<string | null>(null);
  public subscriptionStatus$ = this.subscriptionStatus.asObservable();
  // Emits whenever the stored token changes (login, refresh, silent refresh, clear).
  // Lets UI (e.g. the mobile nav) rebuild against the freshest role/account claims.
  private tokenChanged = new BehaviorSubject<string | null>(null);
  public tokenChanged$ = this.tokenChanged.asObservable();
  private _isInitialized = new BehaviorSubject<boolean>(false);
  public isInitialized$ = this._isInitialized.asObservable();

  get loggedIn(): boolean {
    return !!this._token;
  }

  private tokenRefreshInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient, private router: Router) {
    this.initializeAuth();
  }

  private initializeAuth() {
    // Load token from storage
    this._token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    // If we have a token, validate it
    if (this._token) {
      this.loadToken().subscribe({
        next: (validToken) => {
          if (!validToken) {
            this.clearTokens();
          }
        },
        error: () => this.clearTokens()
      });
    }

    this._isInitialized.next(true);
  }

  // Add this method to ensure token is loaded before making requests
  async ensureTokenInitialized(): Promise<boolean> {
    if (this._token) {
      return true;
    }

    // If we're already initializing, wait for it to complete
    if (this._isInitialized.value) {
      return !!this._token;
    }

    // Wait for initialization to complete
    return firstValueFrom(
      this.isInitialized$.pipe(
        filter(initialized => initialized === true),
        take(1),
        map(() => !!this._token)
      )
    );
  }

  private getDecodedToken(): DecodedToken | null {
    try {
      const token = this.getToken();
      return token ? jwtDecode<DecodedToken>(token) : null;
    } catch (error) {
      //console.error('Error decoding token:', error);
      return null;
    }
  }
  getUserRole(): string | null {
    const decoded = this.getDecodedToken();
    // Check both 'role' and 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role' for backward compatibility
    return decoded?.role ||
           (decoded as any)?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
           null;
  }

  getAccountType(): string {
    const decoded = this.getDecodedToken();
    return decoded?.AccountType || 'full';
  }

  getSubscriptionStatus(): string {
    const decoded = this.getDecodedToken();
    return decoded?.SubscriptionStatus || '';
  }

  getUserEmail(): string {
    const decoded = this.getDecodedToken();
    return decoded?.tokenKey || '';
  }

  logIn(email: string, password: string, rememberMe: boolean): Observable<{ token: string }> {
    return this.http
      .post<{ token: string }>(
        `${environment.apiUrl}/api/Auth/GetToken`,
        { email, password },
        { observe: 'response' }
      )
      .pipe(
        tap((response) => {
          if (response.body?.token) {
            this.saveToken(response.body.token, rememberMe);
          }
        }),
        map(response => response.body!),
        catchError((error: HttpErrorResponse) => {
          // Log the error for debugging
          //console.error('Login error:', error);

          // Create a new error with the server's error message if available
          let errorMessage = 'Login failed. Please try again.';
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            }
          }

          // Create a new error with the extracted message and status code
          const customError = new Error(errorMessage) as any;
          customError.status = error.status;
          customError.error = error.error;

          return throwError(() => customError);
        })
      );
  }


  validateResetToken(token: string): Observable<{ valid: boolean }> {
    return this.http.post<{ valid: boolean }>(
      `${environment.apiUrl}/api/Auth/ValidateResetToken`,
      { token }
    );
  }

  isTokenExpired(): Observable<boolean> {
    const token = localStorage.getItem('authToken');
    if (!token) return new Observable((observer) => observer.next(true));

    return new Observable((observer) => {
      this.validateResetToken(token).subscribe(
        (response) => {
          observer.next(!response.valid);
        },
        (error) => {
          //console.warn('Token validation failed:', error);
          this.logOut();
          observer.next(true);
        }
      );
    });
  }

  requestPasswordReset(email: string) {
    return this.http.post<UserTenantInfo[]>(
      `${environment.apiUrl}/api/Auth/ForgotPassword`,
      { email }
    );
  }

  generatePasswordResetToken(email: string, tenantId: number | null | undefined) {
    if (tenantId === null || tenantId === undefined) {
      return throwError(() => new Error('Tenant ID is required'));
    }

    return this.http.post(
      `${environment.apiUrl}/api/Auth/GeneratePasswordResetToken`,
      { email, tenantId }
    );
  }

  resetPassword(token: string, newPassword: string) {
    return this.http.post(
      `${environment.apiUrl}/api/Auth/ResetPassword`,
      { token, newPassword }
    );
  }

  private updateSubscriptionStatus(token: string | null): void {
    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        this.subscriptionStatus.next(decodedToken.SubscriptionStatus || 'active');
      } catch (error) {
        //console.error('Failed to decode token:', error);
        this.subscriptionStatus.next(null);
      }
    } else {
      this.subscriptionStatus.next(null);
    }
  }

  saveToken(token: string, rememberMe: boolean): void {
    this._token = token;
    // Always store in both initially, but use the appropriate one when reading
    try {
      localStorage.setItem('authToken', token);
      sessionStorage.setItem('authToken', token);
    } catch (error) {
      //console.error('Error saving token:', error);
    }
    this.updateSubscriptionStatus(token);
    this.tokenChanged.next(token);
  }

  loadToken(): Observable<string | null> {
    return new Observable(subscriber => {
      try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

        if (!token) {
          subscriber.next(null);
          subscriber.complete();
          return;
        }

        // Verify token is not expired
        try {
          const decoded: any = jwtDecode(token);
          const now = Date.now() / 1000;

          if (decoded.exp > now) {
            subscriber.next(token);
          } else {
            // Token expired, remove it
            this.clearTokens();
            subscriber.next(null);
          }
        } catch (error) {
          //console.error('Error decoding token:', error);
          this.clearTokens();
          subscriber.next(null);
        }

        subscriber.complete();
      } catch (error) {
        //console.error('Error loading token:', error);
        subscriber.error(error);
      }
    });
  }

  clearTokens(): void {
    this._token = null;
    this.subscriptionStatus.next(null);
    this.tokenChanged.next(null);
    try {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
    } catch (error) {
      //console.error('Error clearing tokens:', error);
    }
  }

  async logOut(): Promise<void> {
    await sessionStorage.removeItem('showLowStockPopup');
    this._user = null;
    this.clearTokens();
    this.router.navigate(['/login']);

  }

  getToken(): string | null {
    if (this._token) return this._token;

    // Try to get token from storage
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    if (token) {
      // Verify token is not expired
      try {
        const decoded: any = jwtDecode(token);
        const now = Date.now() / 1000;

        if (decoded.exp > now) {
          this._token = token;
          return token;
        } else {
          // Token expired, remove it
          this.clearTokens();
        }
      } catch (error) {
        //console.error('Error decoding token:', error);
        this.clearTokens();
      }
    }

    return null;
  }

  // Add this method to get token with a Promise for async operations
  getTokenAsync(): Promise<string | null> {
    return firstValueFrom(
      this.isInitialized$.pipe(
        filter(initialized => initialized === true),
        take(1),
        map(() => this.getToken())
      )
    );
  }

  getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  refreshToken(): Observable<{ token: string }> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('No token available for refresh'));
    }

    // If refresh is already in progress, return the observable
    if (this.tokenRefreshInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((result: string | null) => result !== null),
        take(1),
        map(() => ({ token: this.getToken()! }))
      );
    }

    this.tokenRefreshInProgress = true;
    this.refreshTokenSubject.next(null);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    return this.http.post<{ token: string }>(
      `${environment.apiUrl}/api/Auth/RefreshToken`,
      { token },
      { headers, withCredentials: true } // Important for AWS
    ).pipe(
      tap(response => {
        this.tokenRefreshInProgress = false;
        if (response?.token) {
          const rememberMe = !!localStorage.getItem('authToken');
          this.saveToken(response.token, rememberMe);
          this.refreshTokenSubject.next(response.token);
        }
      }),
      catchError(error => {
        this.tokenRefreshInProgress = false;
        this.refreshTokenSubject.next(null);
        //console.error('Error refreshing token:', error);
        this.logOut();
        this.router.navigate(['/login']);
        return throwError(() => error);
      })
    );
  }

  // Like refreshToken() but never logs out on failure — safe to call mid-session.
  silentRefreshToken(): Observable<void> {
    const token = this.getToken();
    if (!token) return of(undefined);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
    return this.http.post<{ token: string }>(
      `${environment.apiUrl}/api/Auth/RefreshToken`,
      { token },
      { headers, withCredentials: true }
    ).pipe(
      tap(response => {
        if (response?.token) {
          const rememberMe = !!localStorage.getItem('authToken');
          this.saveToken(response.token, rememberMe);
        }
      }),
      map(() => undefined),
      catchError(e => {
        console.warn('Silent token refresh failed:', e);
        return of(undefined);
      })
    );
  }

  getCurrentUserEmail(): string | null {
    const token = this.getToken();
    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        return decodedToken.email || null;
      } catch (error) {
        //console.error('Failed to decode token:', error);
        return null;
      }
    }
    return null;
  }

  getUser(): Observable<any> {
    return this.http.get<any>(
      `${environment.apiUrl}/api/User/GetById?UserId=${localStorage.getItem('user_id')}`
    );
  }
}
